import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { UsePaginationReturn } from "@/hooks/usePagination";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 15, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;
const PAGE_PARAM = "page";
const LIMIT_PARAM = "limit";

function getPageNumbers(
  page: number,
  totalPages: number
): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: (number | "ellipsis")[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  if (start > 2) {
    pages.push("ellipsis");
  }
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  if (end < totalPages - 1) {
    pages.push("ellipsis");
  }
  if (totalPages > 1) {
    pages.push(totalPages);
  }
  return pages;
}

export interface UsePaginationFromUrlOptions {
  pageSizeOptions?: number[];
}

export function usePaginationFromUrl(
  options: UsePaginationFromUrlOptions = {}
): UsePaginationReturn {
  const { pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const [total, setTotal] = useState(0);

  const pageParam = searchParams.get(PAGE_PARAM);
  const limitParam = searchParams.get(LIMIT_PARAM);

  const page = useMemo(() => {
    const parsed = parseInt(pageParam ?? "1", 10);
    return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
  }, [pageParam]);

  const limit = useMemo(() => {
    const parsed = parseInt(limitParam ?? String(DEFAULT_PAGE_SIZE), 10);
    if (Number.isNaN(parsed) || parsed < 1) return DEFAULT_PAGE_SIZE;
    return pageSizeOptions.includes(parsed) ? parsed : DEFAULT_PAGE_SIZE;
  }, [limitParam, pageSizeOptions]);

  const totalPages = Math.ceil(total / limit) || 1;
  const hasNextPage = page < totalPages;
  const hasPrevPage = page > 1;

  const pageNumbers = useMemo(
    () => getPageNumbers(page, totalPages),
    [page, totalPages]
  );

  const goToPage = useCallback(
    (newPage: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(PAGE_PARAM, String(newPage));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setLimit = useCallback(
    (newLimit: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(LIMIT_PARAM, String(newLimit));
          next.set(PAGE_PARAM, "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Stable reference when page/limit/total-derived fields are unchanged — avoids effects
  // that list `pagination` in deps from running every parent render (refetch / 429 storms).
  return useMemo(
    () => ({
      page,
      limit,
      total,
      setTotal,
      totalPages,
      hasNextPage,
      hasPrevPage,
      pageNumbers,
      goToPage,
      setLimit,
      pageSizeOptions,
    }),
    [
      page,
      limit,
      total,
      setTotal,
      totalPages,
      hasNextPage,
      hasPrevPage,
      pageNumbers,
      goToPage,
      setLimit,
      pageSizeOptions,
    ]
  );
}
